import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { restDeleter, restFetcher, restPatcher, restPoster, restPutter } from "~/api/fetchers";
import { FeaturePermission } from "~/api/graphql-types";
import { createFilter } from "~/api/utils";
import { useToast } from "~/components/alerts/toast-provider";
import { useFeatures } from "~/components/features.context";
import { useI18n } from "~/hooks/use-i18n";

const ReminderBaseSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
});

export const ReminderSchema = ReminderBaseSchema.extend({
  sid: z.string(),
  id: z.number(),
  dueDate: z.string(),
  patientId: z.number(),
  teamId: z.number().optional(),
  patient: z.object({
    firstName: z.string(),
    lastName: z.string(),
  }),
  type: z.enum(["REMINDER"]),
});

export const ReminderEditSchema = ReminderBaseSchema.extend({
  title: z.string().min(1, "org:dashboard.reminders.edit.fields.title.required"),
  dueDate: z.date({ required_error: "org:dashboard.reminders.edit.fields.due_date.required" }),
});

export type Reminder = z.infer<typeof ReminderSchema>;
export type ReminderEdit = z.infer<typeof ReminderEditSchema>;

export const GET_ALL_REMINDERS_ENDPOINT = "reminder/getAll";

const allRemindersResponse = z.array(ReminderSchema);
export type AllRemindersResponse = z.infer<typeof allRemindersResponse>;

export type ReminderV2 = {
  id: number;
  description?: string;
  dueDate: string;
  patient: {
    id: number;
    firstName: string;
    lastName: string;
  };
  worker: {
    id: number;
    firstName: string;
    lastName: string;
  };
  title?: string; // Optional because some production data have missing titles
  resolutionDate?: string;
  type: "REMINDER";
  restricted?: boolean;
  sid: string; // TODO: Only to overlap with v1 reminder remove after TAC feature release
};

export type PaginatedAllRemindersResponse = {
  currentPageNumber: number;
  currentPageTotal: number;
  pageSize: number;
  results: Array<ReminderV2>;
  totalPages: number;
  totalResults: number;
};

type PatchOperations = Array<{
  op: string;
  path: string;
  value: string;
}>;

export type ReminderSearchQueryParams = {
  page?: number;
  size?: number;
  clientId?: number;
  userId?: number;
  state?: "ACTIVE" | "RESOLVED";
};

export const reminderV2Queries = {
  all: () => ["reminders"],
  lists: () => [...reminderV2Queries.all(), "list"],
  list: (filters: ReminderSearchQueryParams) => {
    const searchParams = createFilter<ReminderSearchQueryParams>(filters);
    return queryOptions({
      queryKey: [...reminderV2Queries.lists(), filters],
      queryFn: () => restFetcher<PaginatedAllRemindersResponse>("/v2/reminders?" + searchParams),
      placeholderData: keepPreviousData,
    });
  },
  details: () => [...reminderV2Queries.all(), "detail"],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...reminderV2Queries.details(), id],
      queryFn: () => restFetcher<ReminderV2>(`/v2/reminders/${id}`),
    }),
  update: () => ({
    mutationFn: (reminder: Reminder) => {
      const { patientId, patient, ...reminderData } = reminder;
      const v2Reminder = { ...reminderData, patient: { id: patientId, ...patient } };
      return restPutter<Omit<ReminderV2, "worker">>(`/v2/reminders/${reminder.id}`, v2Reminder);
    },
  }),
  complete: () => ({
    mutationFn: ({ id }: { id: number }) =>
      restPatcher<PatchOperations, ReminderV2>(`/v2/reminders/${id}`, [
        { op: "add", path: "/resolutionDate", value: new Date().toISOString() },
      ]),
  }),
  delete: () => ({
    mutationFn: ({ id }: { id: number }) => restDeleter<ReminderV2>(`/v2/reminders/${id}`),
  }),
};

const dashboardDataRefetchSettings = {
  staleTime: 10 * 1000, // Cache data for 10 seconds if refocusing window or returning to page
  keepPreviousData: true, // Keep previous data until the new data is fetched
  refetchOnWindowFocus: true, // refetch stale queries when the window regains focus
  refetchOnMount: true, // or the app programmatically returns to the dashboard
};

export const reminderV1Query = queryOptions({
  queryKey: [GET_ALL_REMINDERS_ENDPOINT],
  queryFn: async () => {
    const resp = await restFetcher<AllRemindersResponse>(`/v1/${GET_ALL_REMINDERS_ENDPOINT}`);
    return allRemindersResponse.parse(resp);
  },
  ...dashboardDataRefetchSettings,
});

export function useUpdateReminderMutation() {
  const queryClient = useQueryClient();
  const { enqueueError, enqueueSuccess } = useToast();
  const { t } = useI18n("org");
  const { featureEnabled } = useFeatures();
  const tacFeatureEnabled = featureEnabled(FeaturePermission.TeamAccessControls);

  const errorMessage = t("org:dashboard.reminders.edit.error");
  const successMessage = t("org:dashboard.reminders.edit.success");

  const options = tacFeatureEnabled
    ? {
        ...reminderV2Queries.update(),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: reminderV2Queries.all() });
          enqueueSuccess(successMessage);
        },
      }
    : {
        mutationFn: (body: Reminder) => restPoster(`/v1/reminder/update`, body),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [GET_ALL_REMINDERS_ENDPOINT] });
          enqueueSuccess(successMessage);
        },
      };

  return useMutation(options);
}

export function useCompleteReminderMutation() {
  const queryClient = useQueryClient();
  const { enqueueError, enqueueSuccess } = useToast();
  const { t } = useI18n("org");
  const { featureEnabled } = useFeatures();
  const tacFeatureEnabled = featureEnabled(FeaturePermission.TeamAccessControls);

  const errorMessage = t("org:dashboard.reminders.actions.complete.error");
  const successMessage = t("org:dashboard.reminders.actions.complete.success");

  const options = tacFeatureEnabled
    ? {
        ...reminderV2Queries.complete(),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: reminderV2Queries.all() });
          enqueueSuccess(successMessage);
        },
      }
    : {
        mutationFn: (body: { id: number }) => restPoster(`/v1/reminder/resolve`, body),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [GET_ALL_REMINDERS_ENDPOINT] });
          enqueueSuccess(successMessage);
        },
      };

  return useMutation(options);
}

export function useDeleteReminderMutation() {
  const queryClient = useQueryClient();
  const { enqueueError, enqueueSuccess } = useToast();
  const { t } = useI18n("org");
  const { featureEnabled } = useFeatures();
  const tacFeatureEnabled = featureEnabled(FeaturePermission.TeamAccessControls);

  const errorMessage = t("org:dashboard.reminders.delete.error");
  const successMessage = t("org:dashboard.reminders.delete.success");

  const options = tacFeatureEnabled
    ? {
        ...reminderV2Queries.delete(),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: reminderV2Queries.all() });
          enqueueSuccess(successMessage);
        },
      }
    : {
        mutationFn: (body: { id: number }) => restPoster(`/v1/reminder/delete`, body),
        onError: () => {
          enqueueError(errorMessage);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [GET_ALL_REMINDERS_ENDPOINT] });
          enqueueSuccess(successMessage);
        },
      };

  return useMutation(options);
}
